import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from '../utils/debounce';

const filter = createFilterOptions();

const getOptionComparableKey = (option, labelKey) => {
  if (option == null) return '';
  if (typeof option === 'string') return option;
  return String(option.id ?? option.value ?? option[labelKey] ?? '');
};

const areOptionsEquivalent = (prevOptions, nextOptions, labelKey) => {
  if (prevOptions === nextOptions) return true;
  if (!Array.isArray(prevOptions) || !Array.isArray(nextOptions)) return false;
  if (prevOptions.length !== nextOptions.length) return false;

  for (let i = 0; i < prevOptions.length; i += 1) {
    if (getOptionComparableKey(prevOptions[i], labelKey) !== getOptionComparableKey(nextOptions[i], labelKey)) {
      return false;
    }
  }

  return true;
};

export function useApiAutocomplete({
  labelKey = 'name',
  onChange,
  getOptionList: apiGetOptionList,
  createOption: apiCreateOption,
  createPrefix,
  getFilterByValue,
  apiParams = {},
  makeFilter,
  name,
  listLimit = 50,
  minChars = 0,
}) {
  const [apiOptions, setApiOptions] = useState({});

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultMakeFilter = useCallback((id, value, others) => {
    const filter = {};
    if (id) filter.id = id;
    if (value) filter.text = value;
    if (others) filter.others = others;
    return filter;
  }, []);

  const getOptionLabel = useCallback(
    (option) => {
      if (!option) {
        return '';
      }
      if (typeof option === 'string') return option;
      if (option.create) return option.create;
      return option[labelKey];
    },
    [labelKey]
  );

  const filterOptions = useCallback(
    (options, state) => {
      const filtered = filter(options, {
        ...state,
        inputValue: state.inputValue.endsWith('*') ? state.inputValue.slice(0, -1) : state.inputValue,
      });
      if (state.inputValue !== '' && !filtered.length && typeof apiCreateOption === 'function') {
        filtered.push({
          create: state.inputValue,
          [labelKey]: `${state.inputValue}`,
          // [labelKey]: `${createPrefix || trans('create')} "${state.inputValue}"`,
        });
      }
      return filtered;
    },
    [labelKey, apiCreateOption]
  );

  const renderOption = useCallback((option) => option[labelKey], [labelKey]);

  const fetchOptionList = useCallback(
    async ({ id, value = '', limit = listLimit, others = false }) => {
      const trimmed = String(value || '').trim();
      if (minChars > 0 && trimmed.length < minChars) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let filterObj = {};
        if (typeof getFilterByValue === 'function') {
          filterObj = { ...getFilterByValue(value) };
        } else {
          filterObj = {
            filter: typeof makeFilter === 'function' ? makeFilter(id, value) : defaultMakeFilter(id, value, others),
          };
        }
        const result = await apiGetOptionList({
          ...filterObj,
          limit,
          order: [labelKey],
          ...apiOptions,
        });
        let items = [];
        if (result && result.payload) {
          items = result.payload.map((i) => ({ create: i.tagName, value: i.tagName }));
        } else if (Array.isArray(result)) {
          items = [...result];
        }
        setOptions((prevOptions) => (areOptionsEquivalent(prevOptions, items, labelKey) ? prevOptions : items));
      } catch (e) {
        console.warn(e.message);
      }
      setLoading(false);
    },
    [apiGetOptionList, makeFilter, defaultMakeFilter, getFilterByValue, labelKey, listLimit, apiOptions, minChars]
  );

  const getOptionList = useMemo(() => {
    return debounce(fetchOptionList, 300);
  }, [fetchOptionList]);

  const handleInputChange = useCallback(
    (e, value, reason) => {
      if (e) e.persist();
      if (reason && reason !== 'input' && reason !== 'clear') return;
      getOptionList({ value });
    },
    [getOptionList]
  );

  const createOption = useCallback(
    async (value) => {
      setLoading(true);
      try {
        const result = await apiCreateOption({ [labelKey]: value });
        setLoading(false);
        return result;
      } catch (e) {
        console.warn(e.message);
        return undefined;
      }
    },
    [labelKey, apiCreateOption]
  );

  const handleValueChange = useCallback(
    (e) => {
      return onChange && onChange(e, name);
    },
    [onChange, name]
  );

  const setApiParams = useCallback(
    (newParams) => {
      setApiOptions({ ...apiOptions, newParams });
      getOptionList({});
    },
    [apiOptions, getOptionList]
  );

  useEffect(() => {
    setApiOptions(apiParams);
    if (!(minChars > 0)) {
      fetchOptionList({});
    }
    return () => {
      getOptionList.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    options,
    loading,
    setApiParams,
    getOptionLabel,
    filterOptions,
    renderOption,
    handleValueChange,
    handleInputChange,
    getOptionList,
    createOption,
  };
}
